import { GlobeFill } from '@lyearn/icons';
import { Stack, Typography } from '@lyearn/ui';
import { ContentsFragment } from '@/modules/corporate/modules/admin/graphql/fragments/generated/Contents';
import SalesConversationCardGrid from '@/modules/knowledge-asset/KnowledgeAssetManager/components/SalesConversationCardGrid';
import SolutionCardGrid from '@/modules/knowledge-asset/KnowledgeAssetManager/components/SolutionCardGrid';
import { KnowledgeAssetContentType } from '@/types/schema';

import EmptyState from '../../../KnowledgeAssetDetails/components/EmptyState';

interface SolutionOverviewProps {
  solutionType: string[];
  solutionConversation: string[];
  solutionCategories: string[];
  solutions: Array<{ node: KnowledgeAssetContentType }>;
  salesConversations: Array<{ node: KnowledgeAssetContentType }>;
}

const SolutionOverview = ({
  solutionType,
  solutionConversation,
  solutionCategories,
  solutions,
  salesConversations,
}: SolutionOverviewProps) => {
  // Flatten and process the arrays to ensure each item is on a new line
  const processItems = (items: string[]) => {
    return items.flatMap((item) => item.split(/,\s*/)).filter(Boolean);
  };

  const processedSolutionType = processItems(solutionType);
  const processedSolutionCategories = processItems(solutionCategories);

  return (
    <Stack className="w-full" direction="col" gapY={32}>
      {/* First Section - Solution Type and Categories */}
      <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
        {/* Solution Type Box */}
        <Stack className="border border-border rounded-12 p-20" direction="col">
          <Stack alignItems="center" className="mb-16" gapX={8}>
            <GlobeFill fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">Solution Type</Typography>
          </Stack>
          <Stack className="flex-wrap" direction="col" gapY={4}>
            {processedSolutionType.length > 0 ? (
              processedSolutionType.map((type, index) => (
                <Typography key={index} className="text-text-secondary" variant="body-long-01">
                  {type}
                </Typography>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>

        {/* Solution Categories Box */}
        <Stack className="border border-border rounded-12 p-20" direction="col">
          <Stack alignItems="center" className="mb-16" gapX={8}>
            <GlobeFill fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">Solution Categories</Typography>
          </Stack>
          <Stack className="flex-wrap" direction="col" gapY={4}>
            {processedSolutionCategories.length > 0 ? (
              processedSolutionCategories.map((category, index) => (
                <Typography key={index} className="text-text-secondary" variant="body-long-01">
                  {category}
                </Typography>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>
      </Stack>

      {/* Second Section - Sales Conversation Cards */}
      <Stack direction="col" gapY={16}>
        <Typography className="pl-4" variant="heading-s">
          Customer Conversation
        </Typography>
        {salesConversations.length > 0 ? (
          <Stack
            className="grid grid-cols-2 gap-16 xs:grid-cols-1"
            style={{ maxWidth: 'fit-content' }}>
            {salesConversations.map((conversation, index) => (
              <SalesConversationCardGrid
                key={index}
                index={index}
                salesConversation={conversation.node}
              />
            ))}
          </Stack>
        ) : (
          <EmptyState />
        )}
      </Stack>

      {/* Third Section - Solution Cards */}
      <Stack direction="col" gapY={16}>
        <Typography className="pl-4" variant="heading-s">
          Solutions
        </Typography>
        {solutions.length > 0 ? (
          <Stack
            className="grid grid-cols-2 gap-16 xs:grid-cols-1"
            style={{ maxWidth: 'fit-content' }}>
            {solutions.map((solution, index) => (
              <SolutionCardGrid
                key={index}
                hideActionMenu
                index={index}
                solution={solution.node as ContentsFragment}
              />
            ))}
          </Stack>
        ) : (
          <EmptyState />
        )}
      </Stack>
    </Stack>
  );
};

export default SolutionOverview;
